import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const TOKEN_KEY = 'seevita_token';

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  role: 'user' | 'admin' | null;
  isAdmin: boolean;
  username: string;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

const getStoredValidToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);

  if (!token) return null;

  if (isTokenExpired(token)) {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }

  return token;
};

interface JwtPayload {
  exp?: number;
  role?: string;
  username?: string;
}

const parseTokenPayload = (token: string): JwtPayload | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64Payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=');
    return JSON.parse(atob(paddedPayload)) as JwtPayload;
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string) => {
  const payload = parseTokenPayload(token);
  if (!payload) return true;
  if (!payload.exp) return false;
  return payload.exp * 1000 <= Date.now();
};

const getRoleFromToken = (token: string | null): 'user' | 'admin' | null => {
  if (!token) return null;
  const payload = parseTokenPayload(token);
  if (!payload) return null;
  return payload.role === 'admin' ? 'admin' : 'user';
};

const getUsernameFromToken = (token: string | null): string => {
  if (!token) return '';
  const payload = parseTokenPayload(token);
  return payload?.username ?? '';
};

const parseApiError = async (response: Response) => {
  let fallback = 'Something went wrong. Please try again.';

  if (response.status === 400) fallback = 'Please check your information and try again.';
  if (response.status === 409) fallback = 'That email or username is already in use.';
  if (response.status === 401) fallback = 'Invalid email or password.';

  try {
    const data = (await response.json()) as { message?: string; error?: string };
    return data.message || data.error || fallback;
  } catch {
    return fallback;
  }
};

const requestAuth = async (path: string, body: Record<string, string>) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 15000);

  let response: Response;

  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('The authentication server is taking too long to respond. Please try again shortly.');
    }

    throw new Error('Unable to reach the authentication server. Please try again shortly.');
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return response;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => getStoredValidToken());

  const getToken = useCallback(() => getStoredValidToken(), []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await requestAuth('/api/auth/login', {
      email: email.trim(),
      password,
    });
    const data = (await response.json()) as { token?: string };

    if (!data.token || isTokenExpired(data.token)) {
      throw new Error('The server returned an invalid session token.');
    }

    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
  }, []);

  const register = useCallback(async (username: string, email: string, password: string) => {
    await requestAuth('/api/auth/register', {
      username: username.trim(),
      email: email.trim(),
      password,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const role = getRoleFromToken(token);

  const value = useMemo<AuthContextType>(
    () => ({
      isLoading: false,
      isAuthenticated: Boolean(token && getStoredValidToken()),
      role,
      isAdmin: role === 'admin',
      username: getUsernameFromToken(token),
      login,
      register,
      logout,
      getToken,
    }),
    [getToken, login, logout, register, token, role],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
